import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FaPercent,
  FaCalendarAlt,
  FaBuilding,
  FaCheckCircle,
  FaSpinner
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { MdDeleteOutline } from "react-icons/md";
import { MdModeEdit } from "react-icons/md";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PayrollHandlingIncentive = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const org_id = searchParams.get('org_id');
  const from_date = searchParams.get('from_date');
  const to_date = searchParams.get('to_date');
  const API_URL = process.env.REACT_APP_FRONTEND_URL;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const monthMap = {
    JANUARY: "01",
    FEBRUARY: "02",
    MARCH: "03",
    APRIL: "04",
    MAY: "05",
    JUNE: "06",
    JULY: "07",
    AUGUST: "08",
    SEPTEMBER: "09",
    OCTOBER: "10",
    NOVEMBER: "11",
    DECEMBER: "12",
  };

  useEffect(() => {
    fetchIncentiveData();
  }, [org_id, from_date, to_date]);

  const fetchIncentiveData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        organization_id: org_id,
        from_date: from_date,
        to_date: to_date,
        page: 1,
        limit: 100
      });

      const response = await fetch(
        `${API_URL}/v1/hris/payroll/incentive-payroll-total?${params}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',

          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch data');

      const result = await response.json();
      setData(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'REJECTED':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center font-montserrat">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-blue-400 rounded-full blur-2xl opacity-30 animate-pulse" />
            <FaSpinner className="relative text-6xl text-blue-500 animate-spin mx-auto mb-4" />
          </div>
          <p className="text-gray-600 text-lg font-medium">Loading incentive data...</p>
        </motion.div>
      </div>
    );
  }
  const getMonthFromGeneratedAt = (date) => {
    return (new Date(date).getMonth() + 1).toString().padStart(2, "0");
  };
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      setIsDeleting(true);

      const payload = {
        organization_id: Number(org_id),
        month: getMonthFromGeneratedAt(deleteTarget.generated_at),
        year: deleteTarget.year,
      };

      const response = await fetch(
        `${API_URL}/v1/hris/payroll/incentive-delete-payroll`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "Delete failed");
      }

      // ✅ SUCCESS TOAST (from backend message)
      toast.success(result.message || "Deleted successfully");

      setShowDeleteModal(false);
      setDeleteTarget(null);
      fetchIncentiveData();
    } catch (err) {
      // ❌ ERROR TOAST
      toast.error(err.message || "Failed to delete incentive payroll");
    } finally {
      setIsDeleting(false);
    }
  };


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-8 font-montserrat"
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Incentive Payroll Processing
            </h1>
            <p className="text-gray-600 text-lg">
              View and manage incentive payroll for the selected period
            </p>
          </div>


        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">


        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-3 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-green-100 to-green-200 p-2 rounded-xl">
              <FaCalendarAlt className="text-green-600 text-2xl" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium mb-1">From Date</p>
              <p className="text-xl font-bold text-gray-800">
                {new Date(from_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-3 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
        >
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-4 rounded-xl">
              <FaCalendarAlt className="text-purple-600 text-2xl" />
            </div>
            <div>
              <p className="text-sm text-gray-500 font-medium mb-1">To Date</p>
              <p className="text-xl font-bold text-gray-800">
                {new Date(to_date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Error State */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border-2 border-red-200 rounded-2xl p-6 mb-8"
        >
          <div className="flex items-center gap-4">
            <div className="bg-red-100 p-4 rounded-xl">
              <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-red-800 mb-1 text-lg">Error Loading Data</h3>
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Data Table */}
      {!error && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
        >
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3">
            <h2 className="text-md font-bold text-white flex items-center gap-3">
              <FaCheckCircle />
              Payroll Records ({data.length})
            </h2>
          </div>

          {data.length === 0 ? (
            <div className="p-16 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-gray-200 rounded-full blur-xl opacity-50" />
                <div className="relative bg-gradient-to-br from-gray-100 to-gray-200 w-32 h-32 rounded-full flex items-center justify-center">
                  <FaPercent className="text-gray-400 text-5xl" />
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No Records Found</h3>
              <p className="text-gray-500 text-lg">
                There are no incentive payroll records for the selected period.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm text-gray-700 uppercase tracking-wider">Month</th>
                    <th className="px-6 py-4 text-left text-sm text-gray-700 uppercase tracking-wider">Year</th>
                    <th className="px-6 py-4 text-left text-sm text-gray-700 uppercase tracking-wider">Organization</th>
                    <th className="px-6 py-4 text-left text-sm text-gray-700 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-left text-sm text-gray-700 uppercase tracking-wider">Generated At</th>
                    <th className="px-6 py-4 text-left text-sm text-gray-700 uppercase tracking-wider">Action</th>

                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.map((record, index) => (
                    <motion.tr
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-300"
                    >
                      <td className="px-6 py-5">
                        <span className=" text-gray-800 text-base">
                          {record.month}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <span className="text-gray-700 font-sm">{record.year}</span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <div className="bg-blue-100 p-2 rounded-lg">
                            <FaBuilding className="text-blue-600" />
                          </div>
                          <span className="text-gray-700 font-sm">{record.organization_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className={`px-2 py-1 rounded-full text-sm  border-2 ${getStatusColor(record.payroll_status)}`}>
                          {record.payroll_status}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3 text-gray-600">
                          <FaCalendarAlt className="text-gray-400 text-lg" />
                          <span className="text-sm font-medium">{formatDate(record.generated_at)}</span>
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex items-center gap-8">
                          <MdDeleteOutline
                            className="text-red-400 text-lg cursor-pointer hover:text-red-600"
                            onClick={() => {
                              setDeleteTarget(record);
                              setShowDeleteModal(true);
                            }}
                          />


                          <MdModeEdit

                            className="text-blue-400 text-lg" />
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl"
          >
            <h3 className="text-xl font-semibold text-gray-800 mb-2">
              Confirm Deletion
            </h3>

            <p className="text-gray-600 mb-4">
              Delete incentive payroll for{" "}
              <span className="font-semibold text-gray-800">
                {deleteTarget?.month} {deleteTarget?.year}
              </span>
              ?
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>

              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-5 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center gap-2"
              >
                {isDeleting && <FaSpinner className="animate-spin" />}
                Yes, Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
      <ToastContainer position="top-right" autoClose={3000} />

    </motion.div>


  );
};

export default PayrollHandlingIncentive;